import { T, useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import ActivityOverlay from '../../_molecules/ActivityOverlay';
import { CenteredContainer } from '../../styled/CenteredContainer';
import Wordcloud from '../Wordcloud/Wordcloud';

const WordcloudView = ({
  gameId,
  roundId,
  isAuthenticated,
  uniqueId,
  refetchData,
  showWordcloudOverride,
  onShowWordcloudChange,
}: {
  gameId: string;
  roundId: string;
  isAuthenticated: boolean;
  uniqueId?: string;
  refetchData?: boolean;
  // When provided, the parent (LiveGame) owns the open/closed state so the
  // clicker can drive it. Uncontrolled fallback keeps the original
  // behaviour for the student/results views.
  showWordcloudOverride?: boolean;
  onShowWordcloudChange?: (next: boolean) => void;
}) => {
  const router = useRouter();
  const isControlled =
    showWordcloudOverride !== undefined && onShowWordcloudChange !== undefined;

  const { t } = useTranslate();
  const [localShowWordcloud, setLocalShowWordcloud] = useState(false);
  useEffect(() => {
    if (isControlled) return;
    setLocalShowWordcloud(false);
    if (
      !isAuthenticated ||
      router.pathname === '/[tenantId]/games/[gameId]/live/results'
    ) {
      setLocalShowWordcloud(true);
    } else {
      setLocalShowWordcloud(false);
    }
  }, [roundId, isAuthenticated, router.pathname, isControlled]);

  const showWordcloud = isControlled ? !!showWordcloudOverride : localShowWordcloud;
  const setShowWordcloud = (next: boolean) => {
    if (isControlled) onShowWordcloudChange?.(next);
    else setLocalShowWordcloud(next);
  };

  const metrics = useMetrics();
  const handleHPress = () => {
    setShowWordcloud(!showWordcloud);
    metrics.logGameEvent('Wordcloud.Pressed.H');
  };
  const session = useSession();

  return (
    <>
      <Hotkeys keyName="h" onKeyUp={handleHPress} />
      <CenteredContainer
        style={{
          width: '100%',
          height: '100%',
          maxHeight: '100vh',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {!showWordcloud && (
          <ActivityOverlay>
            <ActivateActivityContainer>
              <h1
                style={{ textAlign: 'center', width: '100%', color: theme.colors.white }}
              >
                {t('Show word cloud?', 'Show word cloud?')}
              </h1>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Button
                  styles={{ fontSize: '30px', padding: '20px 40px' }}
                  type="orange"
                  onClick={() => {
                    metrics.logEvent('LiveGame.ShowActivity.ShowWordcloud');
                    setShowWordcloud(true);
                  }}
                >
                  <T keyName="Ready" defaultValue="Ready" />
                </Button>
              </div>
            </ActivateActivityContainer>
          </ActivityOverlay>
        )}

        <Wordcloud
          roundId={roundId}
          gameId={gameId}
          isAuthenticated={isAuthenticated}
          uniqueId={uniqueId}
          refetchData={refetchData}
        />
      </CenteredContainer>
    </>
  );
};

const ActivateActivityContainer = styled.div`
  background: ${theme.colors.translucentLightBlack};
  color: ${theme.colors.white};
  border-radius: 20px;
  padding: 40px;
  z-index: ${zIndexes('SHOW_WORDCLOUD_OVERLAY')};
  margin: 1%;
  h1 {
    font-weight: bold;
    font-size: 60px;
  }

  p {
    font-weight: bold;
    font-size: 30px;
  }
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.2);
`;

export default WordcloudView;
